<template>
  <div>
    <v-skeleton-loader
      v-if="isAppLoading"
      v-bind="attrs"
      type="list-item-two-line, list-item-two-line, list-item-two-line"
    ></v-skeleton-loader>

    <div v-else>
      <v-menu v-if="hasData" bottom close-on-click allow-overflow>
        <template v-slot:activator="{ on, attrs }">
          <v-btn v-bind="attrs" v-on="on" outlined>
            <v-icon class="mr-2">mdi-sort</v-icon>
            {{ $vuetify.lang.t("$vuetify.base.sort") }} -
            {{ selectedSortOption.title }}
          </v-btn>
        </template>

        <v-list>
          <v-list-item-group
            @change="sort"
            v-model="selectedSort"
            color="primary"
          >
            <v-list-item v-for="(option, index) in sortOptions" :key="index">
              <v-list-item-title>{{ option.title }}</v-list-item-title>
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </v-menu>

      <v-select 
        class="mt-5"
        @change="changeProduct" 
        v-model="selectedProductId" 
        v-if="products && products.length > 0" 
        label="Choose a product" outlined dense hide-details
        item-text="name" item-value="id" 
        :items="productOptions"/>

      <v-text-field
        dense
        clearable
        @keyup.enter="filter()"
        @click:clear="
          search = '';
          filter();
        "
        @click:append="filter()"
        class="mb-1 mt-5"
        outlined
        :label="$vuetify.lang.t('$vuetify.base.search')"
        :hint="$vuetify.lang.t('$vuetify.base.weightToleranceHint')"
        persistent-hint
        v-model="search"
        @change="filter()"
        append-icon="mdi-magnify"
      />
      <div class="d-flex flex-wrap mb-3 mt-2">
        <v-chip
          class="mr-2 mb-2"
          small
          :color="availableOnly ? 'primary' : ''"
          :outlined="!availableOnly"
          @click="activeTab = 'search'; availableOnly = !availableOnly; filter()"
        >
          {{ $vuetify.lang.t('$vuetify.base.availableOnly') }}
        </v-chip>
        <v-chip
          class="mr-2 mb-2"
          small
          :color="searchByDoc ? 'primary' : ''"
          :outlined="!searchByDoc"
          @click="activeTab = 'search'; searchByDoc = !searchByDoc; if (searchByDoc) searchByWeight = false; filter()"
        >
          {{ $vuetify.lang.t('$vuetify.base.searchByDocument') }}
        </v-chip>
        <v-chip
          class="mr-2 mb-2"
          small
          :color="searchByWeight ? 'primary' : ''"
          :outlined="!searchByWeight"
          @click="activeTab = 'search'; searchByWeight = !searchByWeight; if (searchByWeight) searchByDoc = false; filter()"
        >
          {{ $vuetify.lang.t('$vuetify.base.searchByWeight') }}
        </v-chip>
      </div>
      <!--
        Search vs Suggestions tabs. Search tab is the existing behavior
        (full filtered list + date slider). Suggestions tab shows the
        ranked "team default" picks from /inventory/suggested. Both tabs
        emit the same selection event upstream, so the parent doesn't
        need to know which tab the user picked from.
      -->
      <v-tabs v-model="activeTab" grow class="mt-3">
        <v-tab href="#search">
          {{ $vuetify.lang.t('$vuetify.worksheet.suggestion.tabSearch') }}
          <span v-if="hasData" class="text-caption ml-1">
            ({{ filteredInventories ? filteredInventories.length : 0 }})
          </span>
        </v-tab>
        <v-tab href="#suggestions">
          <v-icon small left>mdi-lightbulb-on-outline</v-icon>
          {{ $vuetify.lang.t('$vuetify.worksheet.suggestion.tabSuggestions') }}
          <span v-if="suggestedItems" class="text-caption ml-1">
            ({{ suggestedItems.length }})
          </span>
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab">
        <!-- Search tab — the existing list -->
        <v-tab-item value="search">
          <v-range-slider
            v-if="hasData && dates && dates.length > 4"
            v-model="dateRange"
            :max="maxDate"
            :min="minDate"
            hide-details
            class="align-center mt-4"
            @change="filter()"
            :key="selectedProductId"
          >
            <template v-slot:prepend>
              <div style="white-space: nowrap" class="text-caption">
                {{ dates[dateRange[0]] | formatDateYear }}
              </div>
            </template>
            <template v-slot:append>
              <div style="white-space: nowrap" class="text-caption">
                {{ dates[dateRange[1] - 1] | formatDateYear }}
              </div>
            </template>
          </v-range-slider>

          <v-list v-if="hasData">
            <div v-if="selectedProduct" class="text-overline text-center">
              {{ selectedProduct.name }} -
              {{
                $vuetify.lang.t(
                  "$vuetify.worksheet.xAvailable",
                  filteredInventories ? filteredInventories.length : 0
                )
              }}
            </div>
            <v-list-item-group color="primary">
              <template v-for="(inventory, index) in filteredInventories">
              <v-list-item
                class="pl-0 pl-sm-2 pl-md-4"
                :key="inventory.id"
                :disabled="(inventory.quantityAvailable - inventory.reservedQuantity) <= 0"
                @click="selectedInventoryChanged(inventory)"
              >
                <v-list-item-icon class="mr-2 mr-md-4"
                  >{{ index + 1 }}.</v-list-item-icon
                >
                <v-list-item-content>
                  <v-list-item-title>
                    <div class="d-flex">
                      <span v-if="products && products.length > 0">{{ inventory.productName}}</span>
                      <v-divider v-if="products && products.length > 0" vertical class="mx-2"/>
                      <span>
                      {{ inventory.quantityAvailable - inventory.reservedQuantity | formatNumber }}
                      {{ selectedProduct ? selectedProduct.measureWeight : "" }}
                      </span>
                    </div>
                    <span class="text-caption" v-if="inventory.reserved"
                      > ({{inventory.reservedQuantity | formatNumber }} {{ selectedProduct ? selectedProduct.measureWeight : "" }} {{ $vuetify.lang.t("$vuetify.worksheet.reserved") }}) </span
                    >
                    <span
                      class="text-caption font-weight-light"
                      v-if="
                        inventory.unitDetails && inventory.unitDetails.length > 0
                      "
                    >
                      ({{
                        $vuetify.lang.t(
                          "$vuetify.worksheet.xAvailable",
                          inventory.unitAvailable
                        )
                      }})</span
                    >
                    <span
                      class="text-caption font-weight-light"
                      v-if="
                        inventory.unitDetails && inventory.unitDetails.length > 0
                        && inventory.reservedByWorksheets && inventory.reservedByWorksheets.length > 0
                      "
                    >
                      ({{
                        $vuetify.lang.t(
                          "$vuetify.worksheet.xReserved",
                          inventory.reservedByWorksheets.length
                        )
                      }})
                      </span
                    >
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    <div class="d-flex">
                      {{ inventory.stockTime | formatDateYear }}
                      (Doc #: {{ inventory.documentNumber }} / ID:
                      {{ inventory.id }})
                    </div>
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
              <v-list-item
                v-for="unit in matchingUnitsFor(inventory)"
                :key="inventory.id + '-u-' + unit.id"
                class="pl-8 pl-sm-10 pl-md-12"
                dense
                @click.stop="selectMatchedUnit(inventory, unit)"
              >
                <v-list-item-icon class="mr-2 mr-md-4">
                  <v-icon small color="primary">mdi-package-variant</v-icon>
                </v-list-item-icon>
                <v-list-item-content class="py-1">
                  <v-list-item-title class="text-body-2">
                    {{ unit.code ? unit.code + ' · ' : '' }}
                    <span class="font-weight-bold">
                      {{ unit.amountPerUnit | formatNumber }}
                      {{ selectedProduct ? selectedProduct.measureWeight : "" }}
                    </span>
                    <span class="text-caption font-weight-light ml-2">
                      ({{ $vuetify.lang.t("$vuetify.base.unit") }} ID: {{ unit.id }})
                    </span>
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              </template>
            </v-list-item-group>
          </v-list>

          <div class="my-8 text-center text--secondary" v-else>No data found</div>
        </v-tab-item>

        <!-- Suggestions tab — team-default ranked picks -->
        <v-tab-item value="suggestions">
          <div v-if="suggestedItems && suggestedItems.length > 0">
            <div class="text-caption text--secondary text-center mt-3 mb-1">
              {{ $vuetify.lang.t('$vuetify.worksheet.suggestion.subtitle') }}
            </div>
            <v-list>
              <v-list-item-group color="primary">
                <v-list-item
                  v-for="(s, idx) in suggestedItems"
                  :key="'sugg-' + s.inventory.id"
                  class="pl-0 pl-sm-2 pl-md-4"
                  :disabled="(s.inventory.quantityAvailable - (s.inventory.reservedQuantity || 0)) <= 0"
                  @click="onSuggestedClick(s)"
                >
                  <v-list-item-icon class="mr-2 mr-md-4">{{ idx + 1 }}.</v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>
                      <div class="d-flex align-center flex-wrap">
                        <span v-if="products && products.length > 0">{{ s.inventory.productName }}</span>
                        <v-divider v-if="products && products.length > 0" vertical class="mx-2"/>
                        <span class="font-weight-bold">
                          {{ s.inventory.quantityAvailable | formatNumber }}
                          {{ selectedProduct ? selectedProduct.measureWeight : "" }}
                        </span>
                      </div>
                      <div class="mt-1 d-flex flex-wrap">
                        <v-chip
                          v-for="r in (s.reasons || [])"
                          :key="'sugg-' + s.inventory.id + '-r-' + r.code"
                          x-small
                          class="mr-1 mb-1"
                          :color="r.code === 'LIMITED_HISTORY' ? 'grey lighten-2' : 'primary lighten-4'"
                        >
                          {{ reasonLabel(r.code) }}
                        </v-chip>
                      </div>
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      <div class="d-flex">
                        {{ s.inventory.stockTime | formatDateYear }}
                        (Doc #: {{ s.inventory.documentNumber }} / ID: {{ s.inventory.id }})
                      </div>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list-item-group>
            </v-list>
          </div>
          <div class="my-8 text-center text--secondary" v-else>
            {{ $vuetify.lang.t('$vuetify.worksheet.suggestion.emptyState') }}
          </div>
        </v-tab-item>
      </v-tabs-items>
    </div>

    <v-bottom-sheet scrollable v-model="showUnits">
      <v-sheet class="text-center" style="max-height: 380px; overflow: scroll">
        <v-btn class="mt-6" text color="red" @click="showUnits = false">
          {{ $vuetify.lang.t("$vuetify.close") }}
        </v-btn>
        <v-divider />
        <div class="mx-6">
          <v-text-field
            dense
            clearable
            @keyup.enter="filterUnit()"
            @click:clear="
              searchUnit = '';
              filterUnit();
            "
            @click:append="filterUnit()"
            outlined
            hide-details
            label="Search Unit"
            v-model="searchUnit"
            @change="filterUnit()"
            append-icon="mdi-magnify"
            v-if="units && units.length > 5"
          />
          <v-switch
            @change="filterUnit()"
            hide-details
            dense
            v-model="showUsed"
            label="Show used"
          />
        </div>
        <div class="text-overline">
          {{ $vuetify.lang.t("$vuetify.worksheet.chooseUnit") }}
        </div>

        <v-list v-if="selectedInventory">
          <v-list-item-group>
            <v-list-item
              v-for="(unit, unitIndex) in filteredUnits"
              :key="unit.id"
              :disabled="unit.reserved || unit.status == 'Used'"
              @click="selectedInventoryUnitChanged(unit)"
            >
              <v-list-item-content>
                <v-list-item-title>
                  {{ unitIndex + 1 }}. {{ unit.code }}
                  <span class="font-weight-bold ml-5"
                    >{{ unit.amountPerUnit | formatNumber }}
                    {{ unit.unitMeasure }}</span
                  >
                  <span
                    class="text-caption font-weight-light"
                    v-if="unit.status == 'Used'"
                  >
                    ({{ $vuetify.lang.t("$vuetify.worksheet.used") }})</span
                  >
                  <span
                    class="text-caption font-weight-light"
                    v-else-if="unit.reserved"
                  >
                    ({{ $vuetify.lang.t("$vuetify.worksheet.reserved") }})</span
                  >
                </v-list-item-title>
                <v-list-item-subtitle v-if="unit.description">
                  {{ unit.description }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </v-sheet>
    </v-bottom-sheet>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { tagReservedInventories } from "@/services/inventoryReservation.js";

export default {
  props: {
    value: {
      type: Object,
      required: false,
      default: null,
    },
    products: {
      type: Array,
      required: false,
    },
    productId: {
      type: Number,
      required: true,
    },
    worksheetId: {
      type: Number,
      required: false,
      default: null,
    },
  },
  data: () => {
    return {
      selectedProductId: null,
      selectedProduct: null,
      // "Team default" suggestions for this worksheet's context. Empty
      // unless /inventory/suggested returns results within 2s. Failure or
      // 204 leaves this empty silently — the picker still works as today.
      suggestedItems: [],
      // Active tab in the picker: 'search' (default, existing behavior)
      // or 'suggestions'. Auto-switches to 'suggestions' on load when
      // /inventory/suggested returns chips.
      activeTab: 'search',
      inventories: [],
      filteredInventories: [],
      filteredUnits: [],
      selectedInventory: null,
      selectedInventoryUnit: null,
      units: [],
      dates: [],
      dateRange: [],
      minDate: 0,
      maxDate: 0,
      search: null,
      searchUnit: null,
      selectedSort: 0,
      showUnits: false,
      showUsed: false,
      productOptions: [],
      availableOnly: true,
      searchByDoc: false,
      searchByWeight: false
    };
  },
  watch: {
    showUnits(val) {
      if (!val) {
        this.selectedInventory = null;
      }
    },
    // When the user starts searching (any non-empty text in the search box)
    // or changes the product filter, snap back to the All Available tab so
    // they see live results. Suggestions tab is suppressed in favor of
    // active intent. We never auto-switch back the other way — once the
    // user picks Search, they stay there until they choose Suggestions.
    search(val) {
      if (val && val.length > 0 && this.activeTab !== 'search') {
        this.activeTab = 'search';
      }
    },
    selectedProductId() {
      if (this.activeTab !== 'search') {
        this.activeTab = 'search';
      }
    },
  },
  computed: {
    ...mapGetters("navigation", ["isAppLoading"]),
    productName() {
      if (this.inventories && this.inventories.length > 0) {
        return this.inventories[0].productName;
      } else {
        return null;
      }
    },
    selectedSortOption() {
      return this.sortOptions[this.selectedSort];
    },
    hasData() {
      return this.filteredInventories && this.filteredInventories.length > 0;
    },
    sortOptions() {
      return [
        {
          title: `${this.$vuetify.lang.t(
            "$vuetify.base.date"
          )} ${this.$vuetify.lang.t("$vuetify.base.sortDescending")}`,
          field: "stockTime",
          order: "desc",
        },
        {
          title: `${this.$vuetify.lang.t(
            "$vuetify.base.date"
          )} ${this.$vuetify.lang.t("$vuetify.base.sortAscending")}`,
          field: "stockTime",
          order: "asc",
        },
        {
          title: `${this.$vuetify.lang.t(
            "$vuetify.base.weight"
          )} ${this.$vuetify.lang.t("$vuetify.base.sortDescending")}`,
          field: "quantityAvailable",
          order: "desc",
        },
        {
          title: `${this.$vuetify.lang.t(
            "$vuetify.base.weight"
          )} ${this.$vuetify.lang.t("$vuetify.base.sortAscending")}`,
          field: "quantityAvailable",
          order: "asc",
        },
      ];
    },
    states: {
      get() {
        return this.$store.state.inventorySelection.states;
      },
      set(value) {
        this.updateStates(value);
      },
    },
  },
  async mounted() {
    // Fire suggestion fetch in parallel with the main flow — don't await.
    // Failures are silent; the picker is fully usable with no panel.
    this.loadSuggestions();

    this.selectedProductId = this.productId;

    this.productOptions = [...[{id: null, name: 'All Products'}], ...this.products];

    if(this.products && this.products.length > 0 && !this.selectedProductId) {
      this.selectedProductId = null;
    }

    if (this.states.productId != this.selectedProductId) {
      this.resetInventorySelectionStates();
      this.states.productId = this.selectedProductId;
    } else {
      this.dateRange = this.states.dateRange;
      this.search = this.states.search;
      this.selectedSort = this.states.selectedSort;
      if (typeof this.states.availableOnly === 'boolean') this.availableOnly = this.states.availableOnly;
      if (typeof this.states.searchByDoc === 'boolean') this.searchByDoc = this.states.searchByDoc;
      if (typeof this.states.searchByWeight === 'boolean') this.searchByWeight = this.states.searchByWeight;
    }

    if(this.states.productId){
      await this.loadCategory(this.states.productId)
        .then((product) => {
          this.selectedProduct = product;
        })
    }

    this.updateAvailableInventory();
  },
  methods: {
    ...mapActions("messages", [
      "addErrorMessage",
      "addMessage",
      "addSuccessMessage",
    ]),
    ...mapActions("inventorySelection", [
      "updateStates",
      "resetInventorySelectionStates",
    ]),
    ...mapActions("categoryStore", ["fetchCategory"]),


    /**
     * Fetch "team default" suggestions for this worksheet. Silent on any
     * failure — 204 (worksheet doesn't qualify), 2s timeout, or 5xx all
     * leave suggestedItems empty and the picker behaves like today.
     */
    async loadSuggestions() {
      if (!this.worksheetId) {
        return;
      }
      try {
        const resp = await this.$axios.get('/inventory/suggested', {
          params: { worksheetId: this.worksheetId, limit: 5 },
          timeout: 2000,
        });
        if (resp.status === 200 && Array.isArray(resp.data) && resp.data.length > 0) {
          this.suggestedItems = resp.data;
          // Auto-switch to the Suggestions tab so operators see them first.
          // They can still click "Search" to fall back to the full list.
          this.activeTab = 'suggestions';
        }
      } catch (err) {
        // Intentionally swallowed — see method comment.
        // eslint-disable-next-line no-console
        console.debug('Suggested-inventory fetch skipped:', err && err.message);
      }
    },

    onSuggestedClick(suggestion) {
      // Reuse the existing single-inventory selection path so the parent's
      // @change handler sees the exact same payload shape as a click on
      // the main list.
      if (suggestion && suggestion.inventory) {
        this.selectedInventoryChanged(suggestion.inventory);
      }
    },

    reasonLabel(code) {
      return this.$vuetify.lang.t(`$vuetify.worksheet.suggestion.reason.${code}`);
    },

    async getAvailableProduct(pIds) {
      if(!pIds || pIds.length < 1){
        return [];
      }
      const ids = pIds.join(",");
      return await this.$axios
        .get(`/inventory/available-by-product`, {params: {productIds: ids }})
        .then((response) => {
          const availableInventories = response.data;
          //Set document number
          if (availableInventories && availableInventories.length > 0) {
            availableInventories.forEach((i) => {
              if (i.fields && i.fields.length > 0) {
                const docNumberField = i.fields.find((f) =>
                  f.field.toLowerCase().includes("document no")
                );
                i.documentNumber = docNumberField
                  ? docNumberField.fieldValueString
                  : "";
              }
            });
          }
          return availableInventories;
        });
    },
    async loadCategory(id) {
      if(id){
        return await this.fetchCategory(id)
          .then((category) => {
            return category;
          })
          .catch((error) => {
            this.addErrorMessage("Item not found; " + error.data.message);
            return null;
          });
      }
      else{ return null }
    },
    updateAvailableInventory(){
      let productIds = []
      if(this.states.productId){
        productIds.push(this.states.productId);
      }
      else{
        productIds = this.products.map((p) => p.id);
      }

      this.getAvailableProduct(productIds).then(async (inventories) => {
        this.inventories = inventories;
        await tagReservedInventories(this.inventories).then(() => {
          this.aggregateDates(inventories);
          this.filter();
          this.sort();
        });
      });
    },
    async changeProduct(){
        this.resetInventorySelectionStates();
        this.states.productId = this.selectedProductId;
        this.dateRange = this.states.dateRange;
        this.search = this.states.search;
        this.selectedSort = this.states.selectedSort;
        this.availableOnly = this.states.availableOnly;
        this.searchByDoc = this.states.searchByDoc;
        this.searchByWeight = this.states.searchByWeight;

        if(this.states.productId){
          await this.loadCategory(this.states.productId)
            .then((product) => {
              this.selectedProduct = product;
            })
        }

        this.updateAvailableInventory();
    },
    filter() {
      this.states.search = this.search;
      this.states.dateRange = this.dateRange;
      this.states.selectedSort = this.selectedSort;
      this.states.availableOnly = this.availableOnly;
      this.states.searchByDoc = this.searchByDoc;
      this.states.searchByWeight = this.searchByWeight;

      if (!this.inventories || this.inventories.length === 0) {
        this.filteredInventories = this.inventories;
        return;
      }

      const term = (this.search || "").trim();
      const normalizedNumeric = term.replace(/,/g, "");
      const numericTerm = term && !isNaN(Number(normalizedNumeric)) ? Number(normalizedNumeric) : null;
      const minDate = new Date(this.dates[this.dateRange[0]]);
      const maxDate = new Date(this.dates[this.dateRange[1] - 1]);

      this.filteredInventories = this.inventories.filter((i) => {
        // date window
        const b = i.stockTime.split(/\D/);
        const stockDate = new Date(`${b[0]}-${b[1]}-${b[2]}`);
        if (stockDate < minDate || stockDate > maxDate) return false;

        // available-only — exclude items with no remaining quantity after reservations
        if (this.availableOnly) {
          const remaining = (i.quantityAvailable || 0) - (i.reservedQuantity || 0);
          if (remaining <= 0) return false;
        }

        if (!term) return true;

        const docMatch = (i.documentNumber || "")
          .toLowerCase()
          .includes(term.toLowerCase());
        const weightMatch =
          numericTerm !== null && (
            Math.abs((i.quantityAvailable || 0) - numericTerm) <= 0.5 ||
            (i.unitDetails && i.unitDetails.some(u =>
              Math.abs((u.amountPerUnit || 0) - numericTerm) <= 0.5
            ))
          );
        const idMatch = String(i.id) === term;

        if (this.searchByDoc && !this.searchByWeight) return docMatch || idMatch;
        if (this.searchByWeight && !this.searchByDoc) return weightMatch;

        // no chip active: auto-detect by input shape
        if (numericTerm !== null) return weightMatch || docMatch || idMatch;
        return docMatch;
      });
    },
    filterUnit() {
      let filtered = [];
      if (this.units && this.units.length > 0) {
        filtered = this.units;

        if (!this.showUsed) {
          filtered = this.units.filter((u) => u.status != "Used");
        }

        filtered = filtered.filter((u) => {
          let matchSearch = true;
          if (this.searchUnit) {
            const searchString = `${u.code} ${u.amountPerUnit}`;
            matchSearch = searchString
              .toLowerCase()
              .includes(this.searchUnit.trim().toLowerCase());
          }
          return matchSearch;
        });
      } else {
        filtered = this.units;
      }

      this.filteredUnits = filtered;
    },
    sort() {
      if (this.filteredInventories) {
        const sortBy = this.selectedSortOption.field;
        const sortOrder = this.selectedSortOption.order;
        this.filteredInventories.sort((a, b) => {
          if (sortBy == "stockTime") {
            const aDate = new Date(a.stockTime);
            const bDate = new Date(b.stockTime);
            return sortOrder == "desc" ? bDate - aDate : aDate - bDate;
          } else if (sortBy == "quantityAvailable") {
            return sortOrder == "desc"
              ? b.quantityAvailable - a.quantityAvailable
              : a.quantityAvailable - b.quantityAvailable;
          }
        });
      }
    },
    aggregateDates(inventories) {
      //parse date min/max
      const dates = new Set();
      inventories.forEach((u) => {
        var b = u.stockTime.split(/\D/);
        const date = `${b[0]}-${b[1]}-${b[2]}`;
        dates.add(date);
      });

      this.dates = [...dates].sort();
      if (this.dateRange == null || this.dateRange.length < 1) {
        this.dateRange = [0, this.dates.length];
      }
      this.minDate = 0;
      this.maxDate = this.dates.length;
    },
    selectedInventoryChanged(inventory) {
      this.selectedInventory = inventory;
      this.selectedInventory = inventory;

      if (
        this.selectedInventory &&
        this.selectedInventory.unitDetails &&
        this.selectedInventory.unitDetails.length > 0
      ) {
        //open inventory unit details
        this.units = [];
        this.showUnits = true;
        this.loadInventoryUnitDetails();
      } else {
        //select inventory
        this.$emit("input", this.selectedInventory);
        this.$emit("change", this.selectedInventory);
      }
    },
    selectedInventoryUnitChanged(unit) {
      this.selectedInventoryUnit = unit;
      console.log("selected inventory unit");
      this.$emit("input", this.selectedInventory);
      this.$emit("change", this.selectedInventory, this.selectedInventoryUnit);
    },
    matchingUnitsFor(inventory) {
      const term = (this.search || "").trim();
      if (!term) return [];
      const normalizedNumeric = term.replace(/,/g, "");
      if (isNaN(Number(normalizedNumeric))) return [];
      if (this.searchByDoc && !this.searchByWeight) return [];
      const numericTerm = Number(normalizedNumeric);
      if (!inventory.unitDetails || inventory.unitDetails.length === 0) return [];
      return inventory.unitDetails.filter(
        (u) =>
          Math.abs((u.amountPerUnit || 0) - numericTerm) <= 0.5 &&
          !u.reserved &&
          u.status !== "Used"
      );
    },
    async selectMatchedUnit(inventory, matchedUnit) {
      this.selectedInventory = inventory;
      await this.loadInventoryUnitDetails();
      const unit = this.units.find((u) => u.id === matchedUnit.id);
      if (!unit) {
        this.addErrorMessage("Unit no longer available");
        return;
      }
      if (unit.status === "Used") {
        this.addErrorMessage("This unit has already been used");
        return;
      }
      if (unit.reserved) {
        this.addErrorMessage("This unit is reserved");
        return;
      }
      this.selectedInventoryUnit = unit;
      this.$emit("input", this.selectedInventory);
      this.$emit("change", this.selectedInventory, this.selectedInventoryUnit);
    },
    async loadInventoryUnitDetails() {
      return await this.$axios(
        "inventory/unit-details/by-inventory/" + this.selectedInventory.id
      ).then((response) => {
        const respUnits = response.data;
        if (respUnits) {
          //set reserved info for unit
          respUnits.forEach((unit) => {
            const inventoryUnit = this.selectedInventory.unitDetails.find(
              (u) => u.id == unit.id
            );
            unit.reserved = inventoryUnit.reserved;
          });
        }

        this.units = respUnits;

        this.filterUnit();
      });
    },
  },
};
</script>

<style></style>
